import { Helmet } from "react-helmet-async";

import { siteConfig } from "@/config/site";
import type { LocaleCode } from "@/i18n/types";
import { LOCALES } from "@/i18n/types";
import { absoluteUrl } from "@/lib/seo-url";
import { getLocaleContent } from "@/lib/site-config";

export interface SeoProps {
  canonical?: string;
  description?: string;
  image?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  locale: LocaleCode;
  noindex?: boolean;
  pathWithoutLocale?: string;
  title?: string;
  type?: "website" | "article";
}

function buildLocaleHref(locale: LocaleCode, pathWithoutLocale: string): string {
  const normalized = pathWithoutLocale === "" || pathWithoutLocale === "/"
    ? ""
    : `/${pathWithoutLocale.replace(/^\/+/, "")}`;
  return absoluteUrl(`/${locale}${normalized}`);
}

export function Seo({
  canonical,
  description,
  image,
  jsonLd,
  locale,
  noindex,
  pathWithoutLocale = "",
  title,
  type = "website",
}: SeoProps): JSX.Element {
  const content = getLocaleContent(siteConfig, locale);
  const { shared } = siteConfig;
  const resolvedTitle = title ? `${title} · ${content.site.name}` : content.site.name;
  const resolvedDescription = description ?? content.site.description;
  const resolvedCanonical = canonical ?? buildLocaleHref(locale, pathWithoutLocale);
  const resolvedImage = absoluteUrl(image ?? shared.defaultOgImage);
  const ldEntries = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  const ogLocaleMap: Record<LocaleCode, string> = {
    en: "en_US",
    es: "es_ES",
  };

  return (
    <Helmet>
      <html lang={locale} />
      <title>{resolvedTitle}</title>
      <meta
        content={resolvedDescription}
        name="description"
      />
      {noindex ? <meta content="noindex,nofollow" name="robots" /> : null}
      <link
        href={resolvedCanonical}
        rel="canonical"
      />
      {LOCALES.map((other) => (
        <link
          key={other}
          href={buildLocaleHref(other, pathWithoutLocale)}
          hrefLang={other}
          rel="alternate"
        />
      ))}
      <link
        href={buildLocaleHref("en", pathWithoutLocale)}
        hrefLang="x-default"
        rel="alternate"
      />

      <meta content={content.site.name} property="og:site_name" />
      <meta content={resolvedTitle} property="og:title" />
      <meta content={resolvedDescription} property="og:description" />
      <meta content={resolvedCanonical} property="og:url" />
      <meta content={type} property="og:type" />
      <meta content={resolvedImage} property="og:image" />
      <meta content={ogLocaleMap[locale] ?? "en_US"} property="og:locale" />
      {LOCALES.filter((other) => other !== locale).map((other) => (
        <meta
          key={other}
          content={ogLocaleMap[other]}
          property="og:locale:alternate"
        />
      ))}

      <meta content="summary_large_image" name="twitter:card" />
      <meta content={resolvedTitle} name="twitter:title" />
      <meta content={resolvedDescription} name="twitter:description" />
      <meta content={resolvedImage} name="twitter:image" />
      {shared.twitterHandle ? (
        <meta content={shared.twitterHandle} name="twitter:site" />
      ) : null}

      {ldEntries.map((entry, index) => (
        <script
          key={index}
          type="application/ld+json"
        >
          {JSON.stringify(entry)}
        </script>
      ))}
    </Helmet>
  );
}
